import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { PasswordInput } from "@/components/form/PasswordInput";
import { FieldError } from "@/components/form/FieldError";
import { SubmitButton } from "@/components/form/SubmitButton";
import { loginSchema, type LoginValues } from "@/features/auth/schemas";
import { useAuth } from "@/features/auth/AuthContext";
import { authApi } from "@/features/auth/api";
import { OtpCodeStep } from "@/components/auth/OtpCodeStep";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, loginWithOtp } = useAuth();
    const [loading, setLoading] = useState(false);

    const redirectTo =
        (location.state as { from?: string } | null)?.from ?? ROUTES.home;

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors, isSubmitted },
    } = useForm<LoginValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: { email: "", password: "", remember: false },
        mode: "onBlur",
    });

    const remember = watch("remember");
    const emailValue = watch("email");
    const [otpMode, setOtpMode] = useState(false);
    const [otpEmail, setOtpEmail] = useState<string | null>(null);

    const sendLoginOtp = async () => {
        const email = (emailValue ?? "").trim().toLowerCase();
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            toast.error("Enter a valid email to receive a code");
            return;
        }
        setLoading(true);
        try {
            await authApi.sendOtp(email, "login");
            setOtpEmail(email);
            toast.success("Code sent — check your email");
        } catch (err) {
            toast.error(formatApiError(err, "Could not send the code"));
        } finally {
            setLoading(false);
        }
    };

    const verifyLoginOtp = async (code: string) => {
        if (!otpEmail) return;
        setLoading(true);
        try {
            const user = await loginWithOtp({ email: otpEmail, code, remember: !!remember });
            toast.success(`Welcome back, ${user.full_name.split(" ")[0] || "friend"}!`);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Verification failed"));
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (values: LoginValues) => {
        setLoading(true);
        try {
            const user = await login({
                email: values.email.trim().toLowerCase(),
                password: values.password,
                remember: !!values.remember,
                portal: "customer",
            });
            toast.success(`Welcome back, ${user.full_name.split(" ")[0] || "friend"}!`);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            toast.error(formatApiError(err, "Sign in failed. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    const onInvalid = () => {
        toast.error("Please fix the highlighted fields");
    };

    return (
        <div data-testid="login-page" className="space-y-8">
            <header>
                <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                    Welcome back
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Sign in to continue shopping on TSGo.
                </p>
            </header>

            {otpEmail ? (
                <OtpCodeStep
                    email={otpEmail}
                    purpose="login"
                    loading={loading}
                    onVerify={verifyLoginOtp}
                    onChangeEmail={() => setOtpEmail(null)}
                    testIdPrefix="login"
                />
            ) : (
            <form
                noValidate
                onSubmit={
                    otpMode
                        ? (e) => {
                              e.preventDefault();
                              void sendLoginOtp();
                          }
                        : handleSubmit(onSubmit, onInvalid)
                }
                aria-label="Login form"
                className="space-y-5"
            >
                <div className="space-y-1.5">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="login-email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            placeholder="you@example.com"
                            className="h-11 pl-9"
                            data-testid="login-email"
                            aria-invalid={!!errors.email || undefined}
                            aria-describedby={
                                errors.email ? "login-email-error" : undefined
                            }
                            disabled={loading}
                            {...register("email")}
                        />
                    </div>
                    <FieldError
                        id="login-email-error"
                        message={errors.email?.message}
                    />
                </div>

                {!otpMode && (
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="login-password">Password</Label>
                        <Link
                            to={ROUTES.forgotPassword}
                            className="text-xs font-semibold text-brand transition hover:underline"
                            data-testid="login-forgot"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    <PasswordInput
                        id="login-password"
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        data-testid="login-password"
                        aria-describedby={
                            errors.password ? "login-password-error" : undefined
                        }
                        error={!!errors.password}
                        disabled={loading}
                        {...register("password")}
                    />
                    <FieldError
                        id="login-password-error"
                        message={errors.password?.message}
                    />
                </div>
                )}

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="login-remember"
                        checked={!!remember}
                        onCheckedChange={(v) =>
                            setValue("remember", v === true, {
                                shouldValidate: isSubmitted,
                            })
                        }
                        data-testid="login-remember"
                        disabled={loading}
                    />
                    <Label
                        htmlFor="login-remember"
                        className="cursor-pointer text-sm font-medium text-foreground/80"
                    >
                        Remember me on this device
                    </Label>
                </div>

                <div className="space-y-3 pt-1">
                    <SubmitButton
                        type="submit"
                        loading={loading}
                        loadingText={otpMode ? "Sending code…" : "Signing in…"}
                        data-testid="login-submit"
                    >
                        {otpMode ? "Send login code" : "Sign in"}
                    </SubmitButton>

                    <button
                        type="button"
                        onClick={() => setOtpMode((m) => !m)}
                        disabled={loading}
                        className="w-full text-center text-sm font-semibold text-brand hover:underline"
                        data-testid="login-toggle-otp"
                    >
                        {otpMode ? "Sign in with password instead" : "Sign in with email code (OTP)"}
                    </button>

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="h-px flex-1 bg-border" />
                        or
                        <span className="h-px flex-1 bg-border" />
                    </div>
                    <GoogleSignInButton disabled={loading} />

                    <SubmitButton
                        type="button"
                        variant="outline"
                        data-testid="login-guest"
                        onClick={() => {
                            toast("Continuing as guest", {
                                description:
                                    "You can browse, but some actions require an account.",
                            });
                            navigate(ROUTES.home);
                        }}
                        disabled={loading}
                    >
                        Continue as guest
                    </SubmitButton>
                </div>
            </form>
            )}

            <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Link
                    to={ROUTES.signup}
                    className="font-semibold text-brand hover:underline"
                    data-testid="login-goto-signup"
                >
                    Sign up
                </Link>
            </p>
        </div>
    );
}
